import React from 'react'
import {
  Button as MuiButtonBase,
  ButtonProps as MuiButtonPropsBase,
} from '@mui/material'
import { useAsyncHandler } from '@/hooks/useAsyncHandler'

export interface MuiButtonProps extends Omit<MuiButtonPropsBase, 'onClick'> {
  /**
   * Optional async click handler that automatically prevents double-clicks.
   * When provided, the button will be disabled during execution.
   * Use this OR onClick, not both.
   */
  asyncOnClick?: () => Promise<void>
  /**
   * Traditional sync click handler. Use asyncOnClick for async operations instead.
   */
  onClick?: MuiButtonPropsBase['onClick']
}

/**
 * MUI Button wrapper with optional async handler support.
 *
 * For async operations, use `asyncOnClick` prop which automatically:
 * - Prevents double-clicks via ref-based locking
 * - Disables button during execution
 * - Manages loading state internally
 *
 * Caller is responsible for error handling and loading indicators.
 * All standard MUI Button props are supported.
 *
 * @example
 * ```tsx
 * // With async handler (recommended for save/submit/delete)
 * <MuiButton
 *   variant="contained"
 *   color="primary"
 *   asyncOnClick={async () => await saveData()}
 * >
 *   Save
 * </MuiButton>
 *
 * // Traditional sync handler
 * <MuiButton
 *   variant="outlined"
 *   onClick={() => console.log('clicked')}
 * >
 *   Cancel
 * </MuiButton>
 * ```
 */
export const MuiButton = React.forwardRef<HTMLButtonElement, MuiButtonProps>(
  ({ asyncOnClick, onClick, disabled, ...props }, ref) => {
    const { run, loading } = useAsyncHandler(asyncOnClick || (async () => {}))

    const handleClick = asyncOnClick
      ? (e: React.MouseEvent<HTMLButtonElement>) => {
          e.preventDefault()
          run()
        }
      : onClick

    return (
      <MuiButtonBase
        onClick={handleClick}
        disabled={disabled || (asyncOnClick ? loading : false)}
        ref={ref}
        {...props}
      />
    )
  },
)

MuiButton.displayName = 'MuiButton'
